/**
 * Testing Sandbox Component
 * Interface for running and viewing agent tests
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Zap,
  TrendingUp,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { custodianClient } from '@/lib/custodianClient';

interface TestingSandboxProps {
  agentId: string;
  agentName: string;
  onTestComplete?: (results: any) => void;
}

export default function TestingSandbox({
  agentId,
  agentName,
  onTestComplete
}: TestingSandboxProps) {
  const [testInput, setTestInput] = useState('');
  const [expectedOutput, setExpectedOutput] = useState('');
  const [testResults, setTestResults] = useState<any>(null);
  const [testHistory, setTestHistory] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Load test history on mount
  useEffect(() => {
    loadTestHistory();
  }, [agentId]);

  const loadTestHistory = async () => {
    try {
      const result = await custodianClient.getTestHistory(agentId, 5);
      if (result.success) {
        setTestHistory(result);
      }
    } catch (error) {
      console.error('Failed to load test history:', error);
    }
  };

  const runTest = async () => {
    if (!testInput.trim()) {
      return;
    }

    setLoading(true);
    setTestResults(null);

    try {
      const result = await custodianClient.runSandboxTest(
        agentId,
        JSON.parse(testInput),
        {
          expected_output: expectedOutput ? JSON.parse(expectedOutput) : undefined,
          evaluate: true
        }
      );

      setTestResults(result);
      
      // Reload history
      await loadTestHistory();

      // Notify parent
      if (onTestComplete && testHistory) {
        onTestComplete(testHistory);
      }
    } catch (error: any) {
      setTestResults({
        success: false,
        passed: false,
        error: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const formatJSON = (str: string) => {
    try {
      return JSON.stringify(JSON.parse(str), null, 2);
    } catch {
      return str;
    }
  };

  return (
    <div className="space-y-6">
      {/* Test Statistics */}
      {testHistory?.stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Success Rate</p>
                  <p className="text-2xl font-bold text-green-600">
                    {testHistory.stats.success_rate.toFixed(1)}%
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Latency</p>
                  <p className="text-2xl font-bold">
                    {testHistory.stats.avg_latency_ms.toFixed(0)}ms
                  </p>
                </div>
                <Clock className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Avg Cost</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${testHistory.stats.avg_cost_usd.toFixed(4)}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Tests</p>
                  <p className="text-2xl font-bold">
                    {testHistory.stats.total_tests}
                  </p>
                </div>
                <Zap className="w-8 h-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Test Input */}
      <Card>
        <CardHeader>
          <CardTitle>Run New Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Test Input (JSON)</label>
            <textarea
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              placeholder='{"query": "Your test input here"}'
              className="w-full h-32 p-3 border rounded-lg font-mono text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Expected Output (Optional, JSON)</label>
            <textarea
              value={expectedOutput}
              onChange={(e) => setExpectedOutput(e.target.value)}
              placeholder='{"result": "Expected output"}'
              className="w-full h-24 p-3 border rounded-lg font-mono text-sm"
            />
          </div>

          <Button
            onClick={runTest}
            disabled={loading || !testInput.trim()}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Test...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run Test
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Test Results */}
      {testResults && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Test Results</CardTitle>
              {testResults.passed ? (
                <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Passed
                </Badge>
              ) : (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <XCircle className="w-4 h-4" />
                  Failed
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Metrics */}
            {testResults.metrics && (
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Latency</p>
                  <p className="text-lg font-bold">{testResults.metrics.latency_ms}ms</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Cost</p>
                  <p className="text-lg font-bold text-green-600">
                    ${testResults.metrics.cost_usd.toFixed(4)}
                  </p>
                </div>
                {testResults.metrics.accuracy_score !== null && (
                  <div>
                    <p className="text-sm text-gray-600">Accuracy</p>
                    <p className="text-lg font-bold">{testResults.metrics.accuracy_score}/100</p>
                  </div>
                )}
              </div>
            )}

            {/* Claude Evaluation */}
            {testResults.evaluation?.commentary && (
              <Alert>
                <AlertDescription>
                  <p className="font-semibold mb-1">Claude's Evaluation</p>
                  <p className="text-sm">{testResults.evaluation.commentary}</p>
                </AlertDescription>
              </Alert>
            )}

            {/* Output */}
            {testResults.output && (
              <div>
                <label className="block text-sm font-medium mb-2">Output</label>
                <pre className="p-3 bg-gray-50 rounded-lg text-sm overflow-x-auto">
                  {JSON.stringify(testResults.output, null, 2)}
                </pre>
              </div>
            )}

            {/* Step Metrics */}
            {testResults.step_metrics && testResults.step_metrics.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-2">Step-by-Step Metrics</label>
                <div className="space-y-2">
                  {testResults.step_metrics.map((step: any, index: number) => (
                    <div key={index} className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{step.step_name}</p>
                        <Badge variant="outline">{step.provider}</Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Latency:</span> {step.latency_ms}ms
                        </div>
                        <div>
                          <span className="font-medium">Cost:</span> ${step.cost_usd.toFixed(4)}
                        </div>
                        <div>
                          <span className="font-medium">Tokens:</span> {step.tokens_used}
                        </div>
                        <div>
                          <span className="font-medium">Model:</span> {step.model}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {testResults.error && (
              <Alert variant="destructive">
                <AlertDescription>
                  <p className="font-semibold">Error</p>
                  <p className="text-sm mt-1">{testResults.error}</p>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Test History */}
      {testHistory?.history && testHistory.history.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowHistory(!showHistory)}>
              <CardTitle>Test History</CardTitle>
              <Button variant="ghost" size="sm">
                {showHistory ? <ChevronUp /> : <ChevronDown />}
              </Button>
            </div>
          </CardHeader>
          {showHistory && (
            <CardContent>
              <div className="space-y-2">
                {testHistory.history.map((test: any) => (
                  <div
                    key={test.test_run_id}
                    className="p-3 border rounded-lg flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      {test.passed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {new Date(test.run_at).toLocaleString()}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-gray-600 mt-1">
                          <span>{test.metrics.latency_ms}ms</span>
                          <span>${test.metrics.cost_usd.toFixed(4)}</span>
                          {test.metrics.accuracy_score && (
                            <span>{test.metrics.accuracy_score}/100</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}

